import { Badge, Button, Input, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Field } from "@/components/form"
import { ErrorState, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { SettingsTitle } from "./settings-general"

type Template = { key: string; name: string; description: string; audience: "customer" | "staff"; subject: string; body_html: string; enabled: boolean; updated_at: string }
type Data = { transport: string; staff_emails: string[]; templates: Template[] }
const KEY = ["settings", "notifications"]

function useNotifications() {
  return useQuery({ queryKey: KEY, queryFn: () => sdk.client.fetch<Data>("/admin/terify/notifications") })
}
function useSaveTemplate(key: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Template> & { reset?: boolean }) => sdk.client.fetch(`/admin/terify/notifications/templates/${key}`, { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function NotificationsSettingsPage() {
  const { data, isLoading, error } = useNotifications()
  const can = useCan()
  const qc = useQueryClient()
  const [emails, setEmails] = useState("")
  useEffect(() => { if (data) setEmails(data.staff_emails.join(", ")) }, [data])
  const saveStaff = useMutation({
    mutationFn: () => sdk.client.fetch("/admin/terify/notifications/staff", { method: "POST", body: { emails: emails.split(/[,\s]+/).filter(Boolean) } }),
    onSuccess: () => { toast.success("Staff recipients saved"); qc.invalidateQueries({ queryKey: KEY }) },
    onError: (e) => toast.error(errorMessage(e)),
  })
  if (error) return <ErrorState error={error} />
  if (isLoading || !data) return <Text size="small">Loading…</Text>
  const group = (aud: "customer" | "staff") => data.templates.filter((t) => t.audience === aud)
  const List = ({ items }: { items: Template[] }) => (
    <ul className="divide-y divide-ui-border-base">
      {items.map((t) => (
        <li key={t.key}>
          <Link to={`/settings/notifications/${t.key}`} className="flex items-center gap-3 px-4 py-3 hover:bg-ui-bg-base-hover">
            <div className="min-w-0 flex-1">
              <Text size="small" weight="plus">{t.name}</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">{t.description}</Text>
            </div>
            <Badge size="2xsmall" color={t.enabled ? "green" : "grey"}>{t.enabled ? "On" : "Off"}</Badge>
          </Link>
        </li>
      ))}
    </ul>
  )
  return (
    <div className="flex flex-col gap-4">
      <SettingsTitle title="Notifications" subtitle={`Emails sent to customers and staff. Delivery: ${({ ses: "Amazon SES", postmark: "Postmark", sendgrid: "SendGrid", klaviyo: "Klaviyo (Settings → Klaviyo)", log: "not set up yet (emails are only written to the server log)" } as Record<string, string>)[data.transport] ?? data.transport}.`} />
      <Link to="/settings/notifications/staff" className="tf-card flex items-center justify-between rounded-[12px] border border-ui-border-base bg-ui-bg-base px-4 py-3 hover:bg-ui-bg-base-hover">
        <span>
          <Text size="small" weight="plus">Your notifications: phone, computer and email alerts</Text>
          <Text size="xsmall" className="text-ui-fg-subtle">New orders, security changes, stock, refunds and problems, pushed to your devices. Send yourself a test.</Text>
        </span>
        <span className="text-ui-fg-interactive text-[13px]">Open →</span>
      </Link>
      <Section title="Customer notifications" className="overflow-hidden"><List items={group("customer")} /></Section>
      <Section title="Staff order notifications" className="overflow-hidden">
        <List items={group("staff")} />
        <div className="border-t border-ui-border-base p-4">
          <Field label="Recipients" hint="Comma-separated emails. Leave empty to use the store email.">
            <Input value={emails} disabled={!can("settings:write")} onChange={(e) => setEmails(e.target.value)} placeholder="ops@yourstore.com" />
          </Field>
          {can("settings:write") && (
            <Button className="mt-3" size="small" variant="secondary" isLoading={saveStaff.isPending} onClick={() => saveStaff.mutate()}>Save recipients</Button>
          )}
        </div>
      </Section>
    </div>
  )
}

export function NotificationTemplatePage() {
  const { key = "" } = useParams()
  const { data, error } = useNotifications()
  const can = useCan()
  const writable = can("settings:write")
  const t = data?.templates.find((x) => x.key === key)
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null)
  const save = useSaveTemplate(key)
  useEffect(() => { if (t) { setSubject(t.subject); setBody(t.body_html) } }, [t?.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced server-side preview (rendered with sample data, same engine as real sends).
  useEffect(() => {
    if (!t) return
    const id = setTimeout(() => {
      sdk.client.fetch<{ subject: string; html: string }>(`/admin/terify/notifications/templates/${key}/preview`, { method: "POST", body: { subject: subject || " ", body_html: body } })
        .then(setPreview).catch(() => {})
    }, 400)
    return () => clearTimeout(id)
  }, [subject, body, key, !!t]) // eslint-disable-line react-hooks/exhaustive-deps

  const test = useMutation({
    mutationFn: () => sdk.client.fetch<{ to: string }>(`/admin/terify/notifications/templates/${key}/send-test`, { method: "POST" }),
    onSuccess: (r) => toast.success(`Test email sent to ${r.to}`),
    onError: (e) => toast.error(errorMessage(e)),
  })
  if (error) return <ErrorState error={error} />
  if (!data) return <Text size="small">Loading…</Text>
  if (!t) return <ErrorState error={new Error("Template not found")} />
  const dirty = subject !== t.subject || body !== t.body_html
  const run = (b: Parameters<typeof save.mutate>[0], msg: string) => save.mutate(b, { onSuccess: () => toast.success(msg), onError: (e) => toast.error(errorMessage(e)) })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/settings/notifications" className="text-[13px] text-ui-fg-subtle hover:underline">← Notifications</Link>
          <SettingsTitle title={t.name} subtitle={t.description} />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[13px]">
            <Switch checked={t.enabled} disabled={!writable || save.isPending} onCheckedChange={(v) => run({ enabled: v }, v ? "Notification turned on" : "Notification turned off")} aria-label="Enabled" />
            {t.enabled ? "On" : "Off"}
          </label>
          {writable && <Button size="small" variant="secondary" isLoading={test.isPending} onClick={() => test.mutate()}>Send test</Button>}
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Email template">
          <div className="flex flex-col gap-3 p-4">
            <Field label="Subject"><Input value={subject} disabled={!writable} maxLength={200} onChange={(e) => setSubject(e.target.value)} /></Field>
            <Field label="Body (HTML)" hint="Placeholders: {{ shop.name }}, {{ order.name }}, {{ customer.first_name }}, {{ order.total }}, {{ line_items }}, {{ shipping_address }}, {{ tracking_number }}, {{ tracking_url }}, {{ refund.amount }}">
              <Textarea rows={18} className="font-mono text-[12px]" value={body} disabled={!writable} onChange={(e) => setBody(e.target.value)} />
            </Field>
            {writable && (
              <div className="flex flex-wrap gap-2">
                <Button size="small" disabled={!dirty || !subject.trim()} isLoading={save.isPending} onClick={() => run({ subject, body_html: body }, "Template saved")}>Save</Button>
                <Button size="small" variant="secondary" disabled={!dirty} onClick={() => { setSubject(t.subject); setBody(t.body_html) }}>Discard</Button>
                <Button size="small" variant="transparent" onClick={() => run({ reset: true }, "Template reset to default")}>Revert to default</Button>
              </div>
            )}
          </div>
        </Section>
        <Section title="Preview">
          <div className="p-4">
            <Text size="small" className="mb-2"><b>Subject:</b> {preview?.subject}</Text>
            {/* Sandboxed: no scripts, no same-origin access. */}
            <iframe title="Email preview" sandbox="" className="h-[560px] w-full rounded border border-ui-border-base bg-white" srcDoc={preview?.html ?? ""} />
          </div>
        </Section>
      </div>
    </div>
  )
}
